import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import _ from "lodash";

import { CheckCircleIcon, DeleteOutlineOutlinedIcon } from "icons";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import IconButton from "components/IconButton";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { openAIDocumentAssistantDelete } from "entities/AISummaries/sdk";
import AISummariesDrawer from "entities/Field/components/Filefield/AISummariesDrawer";
import { IFile } from "entities/File/sdk";
import { usePackageDealDetails } from "entities/Package/sdk";

const AISummaries = () => {
  const [
    documentAssistantIdForAISummariesDrawer,
    setDocumentAssistantIdForAISummariesDrawer,
  ] = useState<number | null>(null);
  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);

  const {
    data: checklist,
    loading: isFetchingChecklist,
    mutate: mutateChecklist,
  } = usePackageDealDetails({
    packageId,
    dealId,
  });
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const documentAssistants: Array<{
    id: number;
    file: IFile;
    prompt_response_count: number;
  }> = useMemo(() => {
    if (_.isNil(checklist)) {
      return [];
    }

    const allDocumentassistants = checklist.tabs
      .flatMap((tab) =>
        tab.categories.flatMap((category) =>
          category.sections.flatMap((section) =>
            section.fields.flatMap((field) =>
              field.values.filter(
                (value) => !_.isEmpty(value?.file?.openai_document_assistants)
              )
            )
          )
        )
      )
      .map((value) => ({
        id: _.get(value, "file.openai_document_assistants.0.id", -1),
        file: value.file as IFile,
        prompt_response_count: _.get(
          value,
          "file.openai_document_assistants.0.prompt_response_count",
          0
        ),
      }));

    return _.uniqBy(allDocumentassistants, "id");
  }, [checklist]);

  const handleAssistantDelete = useCallback(
    ({ assistantId }: { assistantId: number }) => {
      showConfirmationDialog({
        onConfirm: () =>
          openAIDocumentAssistantDelete({ assistantId })
            .then(() => {
              mutateChecklist();
              toast.successMessage("File has been deleted successfully!");
            })
            .catch(handleInvalidRequest),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this file?",
      });
    },
    [mutateChecklist, showConfirmationDialog]
  );

  const promptsCountLabelGet = useCallback(({ count }: { count: number }) => {
    if (count === 1) {
      return `${count} prompt answered`;
    }

    return `${count} prompts answered`;
  }, []);

  const documentsProcessedLabel = useMemo(() => {
    if (isFetchingChecklist) {
      return <Skeleton width={150} sx={{ display: "inline-block" }} />;
    } else if (documentAssistants.length === 1) {
      return `${documentAssistants.length} document processed within this checklist`;
    } else {
      return `${documentAssistants.length} documents processed within this checklist`;
    }
  }, [isFetchingChecklist, documentAssistants]);

  const handleSummaryDelete = useCallback(() => {
    mutateChecklist();
    setDocumentAssistantIdForAISummariesDrawer(null);
  }, [mutateChecklist]);

  return (
    <>
      <Text
        variant="text2"
        color={colors.gray60}
        data-testid="documents-count"
        marginBottom={2}
      >
        {documentsProcessedLabel}
      </Text>

      <Stack spacing={1} data-testid="documents-list">
        {isFetchingChecklist &&
          _.range(4).map((index) => (
            <Stack
              key={index}
              direction="row"
              spacing={2}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2}>
                <Skeleton variant="text" width={30} height={30} />
                <Skeleton variant="text" width={400} height={30} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Skeleton variant="text" width={30} height={30} />
                <Skeleton variant="text" width={30} height={30} />
              </Stack>
            </Stack>
          ))}
        {documentAssistants.map((assistant) => (
          <Stack
            key={assistant.id}
            direction="row"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            data-testid="document-entity"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Completed">
                <CheckCircleIcon sx={{ color: colors.green100 }} />
              </Tooltip>
              <Tooltip title={assistant.file.original_file_name}>
                <Text
                  noWrap
                  variant="text2"
                  fontWeight={500}
                  data-testid="document-name"
                >
                  {_.truncate(assistant.file.original_file_name, {
                    length: 60,
                  })}
                </Text>
              </Tooltip>
              <Text
                variant="text2"
                color={colors.gray60}
                data-testid="documents-count"
                marginBottom={2}
              >
                {promptsCountLabelGet({
                  count: assistant.prompt_response_count,
                })}
              </Text>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="secondary"
                data-testid="view-summary-button"
                onClick={() =>
                  setDocumentAssistantIdForAISummariesDrawer(assistant.id)
                }
              >
                View summary
              </Button>
              <IconButton
                size="small"
                onClick={() =>
                  handleAssistantDelete({
                    assistantId: assistant.id,
                  })
                }
                data-testid="delete-document-button"
              >
                <DeleteOutlineOutlinedIcon
                  sx={{ width: "20px", height: "20px" }}
                />
              </IconButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <AISummariesDrawer
        close={() => setDocumentAssistantIdForAISummariesDrawer(null)}
        documentAssistantId={documentAssistantIdForAISummariesDrawer}
        deleteCallback={handleSummaryDelete}
      />
    </>
  );
};

export default AISummaries;
